<template>
  <CRow>
    <CCol col="12">
      <CCard class="bg-style2">
        <CCardHeader class="bg-gradient-danger text-white" style="border-top-left-radius: 1rem; border-top-right-radius: 1rem ">
          <span class="font-weight-bold h6">
            <CIcon name="cil-list-rich" class="mr-2"/>
            ภาพรวมแผนพัฒนา
          </span>
          <div class="card-header-actions">
            <CButton
              size="sm"
              color="light"
              variant="outline"
              class="export-icon-button"
              :disabled="loading || !items.length"
              title="Export Excel"
              aria-label="Export Excel"
              v-c-tooltip.hover.click="'Export Excel'"
              @click="exportAll"
            >
              <CIcon name="cil-cloud-download"/>
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="3" sm="6" class="mb-3">
              <CCard class="border-left-danger h-100">
                <CCardBody class="text-center">
                  <div class="text-muted small">บุคลากรที่มีแผน</div>
                  <div class="display-4 font-weight-bold">{{ overview.people || 0 }}</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md="3" sm="6" class="mb-3">
              <CCard class="border-left-warning h-100">
                <CCardBody class="text-center">
                  <div class="text-muted small">หัวข้อแผนทั้งหมด</div>
                  <div class="display-4 font-weight-bold">{{ overview.items || 0 }}</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md="3" sm="6" class="mb-3">
              <CCard class="border-left-info h-100">
                <CCardBody class="text-center">
                  <div class="text-muted small">ระดับที่วางแผน</div>
                  <div class="display-4 font-weight-bold">{{ overview.levels || 0 }}</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md="3" sm="6" class="mb-3">
              <CCard class="border-left-success h-100">
                <CCardBody class="text-center">
                  <div class="text-muted small">ดำเนินการแล้ว</div>
                  <div class="display-4 font-weight-bold">{{ overview.completed || 0 }}</div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>

          <CDataTable
            :items="items"
            :fields="fields"
            :items-per-page="10"
            :loading="loading"
            pagination
            sorter
          >
            <template #index="{ index }">
              <td class="text-center">{{ index + 1 }}</td>
            </template>

            <template #people="{ item }">
              <td class="text-center">{{ item.people }}</td>
            </template>

            <template #itemsCount="{ item }">
              <td class="text-center">{{ item.itemsCount }}</td>
            </template>

            <template #levels="{ item }">
              <td class="text-center">{{ item.levels }}</td>
            </template>

            <template #completed="{ item }">
              <td class="text-center">{{ item.completed }} {{ formatPercent(item.completed, item.levels) }}</td>
            </template>

            <template #pending="{ item }">
              <td class="text-center">{{ item.pending }} {{ formatPercent(item.pending, item.levels) }}</td>
            </template>
          </CDataTable>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "PlanSummary",
  props: {
    overview: {
      type: Object,
      default() {
        return {
          people: 0,
          items: 0,
          levels: 0,
          completed: 0,
          pending: 0
        };
      }
    },
    items: {
      type: Array,
      default() {
        return [];
      }
    },
    year: {
      type: Number,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    fields: {
      type: Array,
      default() {
        return [
          {key: "index", label: "#", _style: "width:50px; text-align: center"},
          {key: "category", label: "ประเภท"},
          {key: "title", label: "หัวข้อแผนพัฒนา"},
          {key: "people", label: "People", _style: "width:100px; text-align: center"},
          {key: "itemsCount", label: "Plans", _style: "width:100px; text-align: center"},
          {key: "levels", label: "Levels", _style: "width:100px; text-align: center"},
          {key: "completed", label: "Done", _style: "width:120px; text-align: center"},
          {key: "pending", label: "Pending", _style: "width:120px; text-align: center"}
        ];
      }
    }
  },
  methods: {
    buildOverviewRows() {
      return [
        {metric: "ปี", value: this.year || "-"},
        {metric: "บุคลากรที่มีแผน", value: this.overview.people || 0},
        {metric: "หัวข้อแผนทั้งหมด", value: this.overview.items || 0},
        {metric: "ระดับที่วางแผน", value: this.overview.levels || 0},
        {metric: "ดำเนินการแล้ว", value: this.overview.completed || 0},
        {metric: "ยังไม่แล้วเสร็จ", value: this.overview.pending || 0}
      ];
    },
    buildSummaryRows() {
      return this.items.map((item, index) => ({
        index: index + 1,
        category: item.category,
        title: item.title,
        people: item.people,
        plans: item.itemsCount,
        levels: item.levels,
        completed: item.completed,
        completedPercent: this.formatPercentValue(item.completed, item.levels),
        pending: item.pending,
        pendingPercent: this.formatPercentValue(item.pending, item.levels)
      }));
    },
    exportAll() {
      const workbook = XLSX.utils.book_new();
      const overviewSheet = XLSX.utils.json_to_sheet(this.buildOverviewRows());
      const summarySheet = XLSX.utils.json_to_sheet(this.buildSummaryRows());

      XLSX.utils.book_append_sheet(workbook, overviewSheet, "Overview");
      XLSX.utils.book_append_sheet(workbook, summarySheet, "PlanSummary");
      XLSX.writeFile(workbook, `idp-plan-summary-${this.year || "report"}.xlsx`);
    },
    formatPercent(value, total) {
      return `( ${this.formatPercentValue(value, total)}% )`;
    },
    formatPercentValue(value, total) {
      const numerator = Number(value || 0);
      const denominator = Number(total || 0);

      if (!denominator) {
        return "0.00";
      }

      return ((numerator / denominator) * 100).toFixed(2);
    }
  }
}
</script>

<style scoped>
.border-left-danger {
  border-left: 4px solid #e55353;
}

.border-left-warning {
  border-left: 4px solid #f9b115;
}

.border-left-info {
  border-left: 4px solid #39f;
}

.border-left-success {
  border-left: 4px solid #2eb85c;
}

.export-icon-button {
  align-items: center;
  background: #2f9bff;
  border-color: #2f9bff;
  border-radius: 50%;
  box-shadow: 0 8px 18px rgba(47, 155, 255, 0.28);
  color: #fff;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  padding: 0;
  width: 36px;
}

.export-icon-button:hover,
.export-icon-button:focus {
  background: #168af0;
  border-color: #168af0;
  color: #fff;
}

.export-icon-button:disabled {
  background: #8ecaff;
  border-color: #8ecaff;
  box-shadow: none;
  color: rgba(255, 255, 255, 0.78);
}
</style>
